import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MediaCard from '@/components/feature/MediaCard';
import Pagination from '@/components/feature/Pagination';
import EmptyState from '@/components/feature/EmptyState';
import FilterBar, { type FilterValue } from '@/pages/category/components/FilterBar';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  CATEGORY_META,
  filterMedia,
  formatDate,
  getMediaByKind,
  getNews,
  MOVIE_GENRES,
  newsPath,
  sortNewsByDate,
  TV_GENRES
} from '@/lib/catalog';
import type { News } from '@/types';

const PAGE_SIZE = 8;

const DEFAULT_FILTER: FilterValue = { genres: [], region: '', year: '', sort: 'newest' };

export default function Category() {
  const { type = 'movie' } = useParams();
  const navigate = useNavigate();
  const meta = CATEGORY_META[type];
  const isNews = type === 'news';
  const isMedia = type === 'movie' || type === 'tv';

  const [filter, setFilter] = useState<FilterValue>(DEFAULT_FILTER);
  const [newsOrder, setNewsOrder] = useState<'newest' | 'oldest'>('newest');
  const [page, setPage] = useState(1);

  useDocumentTitle(
    meta ? meta.title : '影视分类 - 风驰影院',
    meta ? meta.description : '风驰影院影视分类列表'
  );

  useEffect(() => {
    setFilter(DEFAULT_FILTER);
    setNewsOrder('newest');
    setPage(1);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [filter, newsOrder]);

  const mediaList = useMemo(() => {
    if (!isMedia) return [];
    return filterMedia(getMediaByKind(type as 'movie' | 'tv'), filter);
  }, [isMedia, type, filter]);

  const newsList = useMemo(() => {
    if (!isNews) return [];
    return sortNewsByDate(getNews(), newsOrder);
  }, [isNews, newsOrder]);

  const total = isNews ? newsList.length : mediaList.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pagedMedia = mediaList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pagedNews = newsList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!meta) {
    return (
      <div className="mx-auto w-full max-w-[1240px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="你访问的影视分类不存在，请从导航栏选择正确的分类。"
          actionLabel="返回首页"
          onAction={() => navigate('/')}
        />
      </div>
    );
  }

  const genreOptions = type === 'movie' ? MOVIE_GENRES : TV_GENRES;

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-6 md:py-10">
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500 md:text-sm" aria-label="面包屑">
        <Link to="/" className="transition hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">{meta.name}</span>
      </nav>

      <header className="mt-4 flex flex-col gap-2 border-b border-background-200 pb-5">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          {isNews ? '影视资讯' : `最新${meta.name}`}
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-foreground-500">{meta.description}</p>
      </header>

      {isMedia ? (
        <div className="mt-5">
          <FilterBar
            genreOptions={genreOptions}
            value={filter}
            onChange={setFilter}
            onReset={() => setFilter(DEFAULT_FILTER)}
          />
        </div>
      ) : (
        <div className="mt-5 flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3">
          <span className="text-sm text-foreground-600">共 {total} 条资讯</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setNewsOrder('newest')}
              className={`rounded-full px-3 py-1.5 text-xs transition md:text-sm ${
                newsOrder === 'newest' ? 'bg-primary-500 text-background-50' : 'border border-background-300 text-foreground-700'
              }`}
            >
              最新发布
            </button>
            <button
              type="button"
              onClick={() => setNewsOrder('oldest')}
              className={`rounded-full px-3 py-1.5 text-xs transition md:text-sm ${
                newsOrder === 'oldest' ? 'bg-primary-500 text-background-50' : 'border border-background-300 text-foreground-700'
              }`}
            >
              最早发布
            </button>
          </div>
        </div>
      )}

      <div className="mt-6">
        {isMedia && (
          <>
            <p className="mb-4 text-sm text-foreground-500">
              共找到 <span className="font-bold text-primary-600">{total}</span> 部{meta.name}
            </p>
            {pagedMedia.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
                {pagedMedia.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <EmptyState
                title="暂无符合条件的内容"
                description="当前筛选条件下没有找到匹配的影视作品，试试调整或重置筛选条件。"
                actionLabel="重置筛选"
                onAction={() => setFilter(DEFAULT_FILTER)}
              />
            )}
          </>
        )}

        {isNews && (
          <>
            {pagedNews.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {pagedNews.map((item: News) => (
                  <Link
                    key={item.id}
                    to={newsPath(item)}
                    className="group flex gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition hover:border-primary-300"
                  >
                    <div className="h-24 w-32 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} 影视资讯`}
                        className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-xs font-medium text-accent-700">{item.category}</span>
                      <h3 className="mt-1 line-clamp-2 text-sm font-bold text-foreground-900 transition group-hover:text-primary-600 md:text-base">
                        {item.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 hidden text-xs leading-relaxed text-foreground-500 sm:block">
                        {item.summary}
                      </p>
                      <span className="mt-auto flex items-center gap-1 pt-1 text-xs text-foreground-400">
                        <i className="ri-time-line" />
                        {formatDate(item.date)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState icon="ri-newspaper-line" title="暂无资讯内容" description="当前分类下暂时还没有资讯，请稍后再来查看。" />
            )}
          </>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}